import { formatDateTime } from '../../lib/utils';
/**
 * User Investigation Card
 * 
 * Displays user profile, risk assessment, devices, and sign-in history.
 */

import { useState } from 'react';
import {
  User,
  Mail,
  Briefcase,
  Building2,
  Shield,
  Clock,
  MapPin,
  Monitor,
  Globe,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Smartphone,
  Laptop,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { RiskBadge } from './RiskBadge';
import {
  formatLocation,
  getSignInStatusInfo,
} from '../../services/investigation';
import type { UserInvestigationResult, InvestigationSignIn, InvestigationDevice } from '../../data/types';

interface UserInvestigationCardProps {
  result: UserInvestigationResult;
}

export function UserInvestigationCard({ result }: UserInvestigationCardProps) {
  const { user, risk, signIns, devices, recentLocations, cachedAt } = result;
  const [showAllSignIns, setShowAllSignIns] = useState(false);

  const displayedSignIns = showAllSignIns ? signIns : signIns.slice(0, 10);

  return (
    <div className="space-y-4">
      {/* User Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-accent-blue" />
              User Profile
            </CardTitle>
            {user.accountEnabled ? (
              <Badge variant="green">
                <CheckCircle2 className="w-3 h-3" />
                Active
              </Badge>
            ) : (
              <Badge variant="red">
                <XCircle className="w-3 h-3" />
                Disabled
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center">
                <User className="w-6 h-6 text-accent-blue" />
              </div>
              <div>
                <p className="text-lg font-medium text-text-primary">{user.displayName}</p>
                <p className="text-sm text-text-secondary font-mono">{user.userPrincipalName}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-default">
              {user.mail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-text-tertiary" />
                  <span className="text-text-secondary truncate">{user.mail}</span>
                </div>
              )}
              {user.jobTitle && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-text-tertiary" />
                  <span className="text-text-secondary truncate">{user.jobTitle}</span>
                </div>
              )}
              {user.department && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-text-tertiary" />
                  <span className="text-text-secondary truncate">{user.department}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Locations Card */}
      {recentLocations && recentLocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent-blue" />
              Recent Sign-In Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {recentLocations.map((location, idx) => (
                <Badge key={idx} variant="default">
                  <MapPin className="w-3 h-3" />
                  {location}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Assessment Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent-blue" />
            Risk Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          {risk ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Risk Level</span>
                <RiskBadge level={risk.riskLevel} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Risk State</span>
                <span className="text-sm text-text-primary capitalize">
                  {risk.riskState.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
              {risk.riskDetail && risk.riskDetail !== 'none' && (
                <div className="pt-2 border-t border-border-default">
                  <span className="text-sm text-text-secondary">Details</span>
                  <p className="text-sm text-text-primary mt-1">
                    {risk.riskDetail.replace(/([A-Z])/g, ' $1').trim()}
                  </p>
                </div>
              )}
              {risk.riskLastUpdatedDateTime && (
                <div className="flex items-center gap-2 text-xs text-text-tertiary">
                  <Clock className="w-3 h-3" />
                  Last updated: {formatDateTime(risk.riskLastUpdatedDateTime)}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-text-secondary">
              <CheckCircle2 className="w-5 h-5 text-accent-green" />
              <span>No risk data available for this user</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Devices Card */}
      {devices && devices.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Laptop className="w-5 h-5 text-accent-blue" />
                Registered Devices
              </CardTitle>
              <span className="text-sm text-text-tertiary">
                {devices.length} device{devices.length !== 1 ? 's' : ''}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {devices.map((device) => (
                <DeviceRow key={device.id} device={device} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sign-In History Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent-blue" />
              Sign-In History
            </CardTitle>
            <span className="text-sm text-text-tertiary">
              Last 90 days ({signIns.length} sign-ins)
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {result.sources?.signIns.status === 'error' ? (
            <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Sign-in history could not be retrieved
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 break-words">
                {result.sources.signIns.error}
              </p>
              <p className="text-xs text-text-secondary mt-2">
                Usually the app registration lacks AuditLog.Read.All, or the tenant has no Entra ID P1 licence.
              </p>
            </div>
          ) : signIns.length === 0 ? (
            <div className="text-center py-4">
              <Globe className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
              <p className="text-text-secondary text-sm">
                {result.sources ? 'Query succeeded — no sign-ins recorded in the last 90 days' : 'No sign-in history available'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedSignIns.map((signIn) => (
                <SignInRow key={signIn.id} signIn={signIn} />
              ))}

              {signIns.length > 10 && (
                <button
                  onClick={() => setShowAllSignIns(!showAllSignIns)}
                  className="flex items-center gap-1 text-sm text-accent-blue hover:underline mt-2"
                >
                  {showAllSignIns ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show all {signIns.length} sign-ins
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cache Info */}
      <p className="text-xs text-text-tertiary text-center">
        Data cached at {formatDateTime(cachedAt)}
      </p>
    </div>
  );
}

// ============================================================================
// DEVICE ROW COMPONENT
// ============================================================================

function DeviceRow({ device }: { device: InvestigationDevice }) {
  return (
    <div className="flex items-center justify-between p-3 bg-bg-secondary border border-border-default rounded-lg">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-bg-tertiary rounded">
          {device.operatingSystem?.toLowerCase().includes('windows') ? (
            <Monitor className="w-4 h-4 text-accent-blue" />
          ) : device.operatingSystem?.toLowerCase().includes('ios') || device.operatingSystem?.toLowerCase().includes('android') ? (
            <Smartphone className="w-4 h-4 text-accent-blue" />
          ) : (
            <Laptop className="w-4 h-4 text-accent-blue" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-text-primary">{device.displayName}</p>
          <p className="text-xs text-text-tertiary">
            {device.operatingSystem} {device.operatingSystemVersion}
            {device.manufacturer && ` • ${device.manufacturer}`}
            {device.model && ` ${device.model}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {device.isCompliant !== null && (
          device.isCompliant ? (
            <Badge variant="green">
              <CheckCircle2 className="w-3 h-3" />
              Compliant
            </Badge>
          ) : (
            <Badge variant="orange">
              <XCircle className="w-3 h-3" />
              Non-Compliant
            </Badge>
          )
        )}
        {device.isManaged && (
          <Badge variant="blue">
            <Shield className="w-3 h-3" />
            Managed
          </Badge>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SIGN-IN ROW COMPONENT
// ============================================================================

function SignInRow({ signIn }: { signIn: InvestigationSignIn }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusInfo = getSignInStatusInfo(signIn.status.errorCode);
  const hasRisk = signIn.riskLevelDuringSignIn && signIn.riskLevelDuringSignIn !== 'none';

  return (
    <div className="border border-border-default rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-bg-secondary transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          {statusInfo.isSuccess ? (
            <CheckCircle2 className="w-4 h-4 text-accent-green flex-shrink-0" />
          ) : (
            <XCircle className="w-4 h-4 text-accent-red flex-shrink-0" />
          )}

          {/* Time and App */}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-primary">
                {formatDateTime(signIn.createdDateTime)}
              </span>
              {hasRisk && (
                <AlertTriangle className="w-3 h-3 text-accent-orange" />
              )}
            </div>
            <span className="text-xs text-text-tertiary">{signIn.appDisplayName || 'Unknown App'}</span>
          </div>
        </div>

        {/* Location and IP */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-text-secondary">
            <MapPin className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{formatLocation(signIn.location)}</span>
          </div>
          <span className="text-text-tertiary font-mono text-xs">{signIn.ipAddress}</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border-default bg-bg-secondary/50">
          <div className="grid grid-cols-2 gap-3 pt-3">
            {/* Client App */}
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4 text-text-tertiary" />
              <div>
                <span className="text-text-tertiary">Client: </span>
                <span className="text-text-secondary">{signIn.clientAppUsed || 'Unknown'}</span>
              </div>
            </div>

            {/* Device */}
            {signIn.deviceDetail.operatingSystem && (
              <div className="flex items-center gap-2 text-sm">
                <Monitor className="w-4 h-4 text-text-tertiary" />
                <div>
                  <span className="text-text-tertiary">OS: </span>
                  <span className="text-text-secondary">{signIn.deviceDetail.operatingSystem}</span>
                </div>
              </div>
            )}

            {/* Browser */}
            {signIn.deviceDetail.browser && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-text-tertiary" />
                <div>
                  <span className="text-text-tertiary">Browser: </span>
                  <span className="text-text-secondary">{signIn.deviceDetail.browser}</span>
                </div>
              </div>
            )}

            {/* Conditional Access */}
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-text-tertiary" />
              <div>
                <span className="text-text-tertiary">CA Status: </span>
                <span className="text-text-secondary capitalize">
                  {signIn.conditionalAccessStatus.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
            </div>

            {/* Error if failed */}
            {!statusInfo.isSuccess && signIn.status.failureReason && (
              <div className="col-span-2 flex items-start gap-2 text-sm">
                <XCircle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-text-tertiary">Failure: </span>
                  <span className="text-text-secondary">{signIn.status.failureReason}</span>
                </div>
              </div>
            )}

            {/* Risk Events */}
            {signIn.riskEventTypes.length > 0 && (
              <div className="col-span-2 flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-accent-orange flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-text-tertiary">Risk Events: </span>
                  <span className="text-text-secondary">
                    {signIn.riskEventTypes.map(r => r.replace(/([A-Z])/g, ' $1').trim()).join(', ')}
                  </span>
                </div>
              </div>
            )}

            {/* Device Compliance */}
            {signIn.deviceDetail.isCompliant !== null && (
              <div className="flex items-center gap-2 text-sm">
                {signIn.deviceDetail.isCompliant ? (
                  <CheckCircle2 className="w-4 h-4 text-accent-green" />
                ) : (
                  <XCircle className="w-4 h-4 text-accent-orange" />
                )}
                <span className="text-text-secondary">
                  {signIn.deviceDetail.isCompliant ? 'Compliant Device' : 'Non-Compliant Device'}
                </span>
              </div>
            )}

            {/* Device Managed */}
            {signIn.deviceDetail.isManaged !== null && (
              <div className="flex items-center gap-2 text-sm">
                {signIn.deviceDetail.isManaged ? (
                  <CheckCircle2 className="w-4 h-4 text-accent-green" />
                ) : (
                  <XCircle className="w-4 h-4 text-text-tertiary" />
                )}
                <span className="text-text-secondary">
                  {signIn.deviceDetail.isManaged ? 'Managed Device' : 'Unmanaged Device'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
